import type React from "react"
import { ChevronRight, type LucideIcon } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Badge } from "@/shared/components/ui/badge"
import { Separator } from "@/shared/components/ui/separator"
import { cn } from "@/shared/lib/utils"

interface ActionButton {
  label: string
  icon?: LucideIcon
  onClick: () => void
  variant?: "default" | "outline" | "ghost" | "destructive"
  disabled?: boolean
  loading?: boolean
}

interface BadgeInfo {
  label: string
  variant?: "default" | "secondary" | "destructive" | "outline"
  className?: string
}

interface AdminPageHeaderProps {
  title: string
  description?: string
  icon?: LucideIcon
  breadcrumbs?: { label: string; href?: string }[]
  actions?: ActionButton[]
  badge?: BadgeInfo
  children?: React.ReactNode
  className?: string
}

/**
 * AdminPageHeader - A consistent header component for all admin pages
 */
export const AdminPageHeader: React.FC<AdminPageHeaderProps> = ({
  title,
  description,
  icon: Icon,
  breadcrumbs,
  actions = [],
  badge,
  children,
  className
}) => {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center space-x-1 text-sm text-muted-foreground" aria-label="Breadcrumb">
          {breadcrumbs.map((crumb, index) => (
            <div key={crumb.label} className="flex items-center">
              {index > 0 && <ChevronRight className="h-4 w-4 mx-1" />}
              {crumb.href ? (
                <a href={crumb.href} className="hover:text-foreground">
                  {crumb.label}
                </a>
              ) : (
                <span className="text-foreground font-medium">{crumb.label}</span>
              )}
            </div>
          ))}
        </nav>
      )}

      {/* Main Header Row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          {/* Icon */}
          {Icon && (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <Icon className="h-5 w-5" />
            </div>
          )}
          
          {/* Title & Description */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">
                {title}
              </h1>
              {badge && (
                <Badge variant={badge.variant || "secondary"} className={badge.className}>
                  {badge.label}
                </Badge>
              )}
            </div>
            {description && (
              <p className="text-sm text-muted-foreground max-w-2xl">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {actions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {actions.map((action, index) => {
              const ActionIcon = action.icon
              
              return (
                <Button
                  key={`${action.label}-${index}`}
                  variant={action.variant || "default"}
                  onClick={action.onClick}
                  disabled={action.disabled || action.loading}
                  className="gap-2"
                >
                  {action.loading ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : ActionIcon ? (
                    <ActionIcon className="h-4 w-4" />
                  ) : null}
                  {action.label}
                </Button>
              )
            })}
          </div>
        )}
      </div>

      {/* Optional children slot */}
      {children && (
        <>
          <Separator />
          <div className="pt-2">
            {children}
          </div>
        </>
      )}
    </div>
  )
}

export default AdminPageHeader
